// Hamstring Template Configuration
// Questions simplified to 12-year-old reading level

export const hamstringTemplate = {
  id: 'HAMSTRING',
  name: 'Hamstring',
  icon: '🦵',
  dominantSide: 'leg',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Which leg has the hamstring problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    injuryDate: {
      type: 'date',
      label: 'When did your hamstring problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly during activity', 'Slowly over time'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Pain at the back of my thigh',
        'Tightness',
        'Swelling',
        'Bruising',
        'Weakness',
        'A popping or tearing feeling',
        'Stiffness',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Please describe the other problem:',
      placeholder: 'Tell us about your other problem',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your hamstring problem started',
      rows: 3,
      required: true
    },

    // Mechanism Section
    activityWhenInjured: {
      type: 'text',
      label: 'What were you doing when it happened?',
      placeholder: 'e.g., sprinting, kicking a ball, stretching'
    },

    feltPop: {
      type: 'radio',
      label: 'Did you feel or hear a "pop" or tearing feeling?',
      options: ['Yes', 'No']
    },

    immediateSymptoms: {
      type: 'radio',
      label: 'Did you have sudden pain and have to stop what you were doing?',
      options: ['Yes', 'No']
    },

    injuryType: {
      type: 'radio',
      label: 'Did you get hit or was it a non-contact injury?',
      options: ['Someone/something hit me', 'No contact - it just happened']
    },

    // Pain Section
    painLocation: {
      type: 'radio',
      label: 'Where is the pain in your thigh?',
      options: [
        'High up near my bottom',
        'Middle of the back of my thigh',
        'Lower down near my knee'
      ]
    },

    painType: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Sharp', 'Dull', 'Aching', 'Cramping', 'Burning', 'Other']
    },

    painTypeOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painType', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painRadiation: {
      type: 'radio',
      label: 'Does the pain spread anywhere else?',
      options: ['Yes, to my bottom', 'Yes, to my knee', 'Yes, to my calf', 'No, just my hamstring']
    },

    painTiming: {
      type: 'checkbox-multiple',
      label: 'When is your pain worst?',
      options: [
        'When running or sprinting',
        'When stretching',
        'All the time',
        'Sitting down',
        'When standing for a long time',
        'After exercise'
      ]
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Running',
        'Sprinting',
        'Bending forward',
        'Squatting',
        'Sitting for a long time',
        'Stretching',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Ice',
        'Gentle stretching',
        'Massage',
        'Pain medication',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Swelling & Bruising
    hasSwelling: {
      type: 'radio',
      label: 'Do you have any swelling?',
      options: ['Yes', 'No']
    },

    swellingLocation: {
      type: 'text',
      label: 'Where is the swelling?',
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    hasBruising: {
      type: 'radio',
      label: 'Do you have any bruising?',
      options: ['Yes', 'No']
    },

    bruisingLocation: {
      type: 'text',
      label: 'Where is the bruising?',
      showWhen: { field: 'hasBruising', equals: 'Yes' }
    },

    bruisingTiming: {
      type: 'text',
      label: 'How soon after the injury did the bruising appear?',
      placeholder: 'e.g., straight away, next day, few days later',
      showWhen: { field: 'hasBruising', equals: 'Yes' }
    },

    // Stiffness & Tightness
    hasTightness: {
      type: 'radio',
      label: 'Does your hamstring feel tight?',
      options: ['Yes', 'No']
    },

    tightnessTiming: {
      type: 'checkbox-multiple',
      label: 'When does it feel tight?',
      options: ['In the morning', 'After sitting/resting', 'After exercise', 'All the time'],
      showWhen: { field: 'hasTightness', equals: 'Yes' }
    },

    tightnessChange: {
      type: 'radio',
      label: 'Is the tightness:',
      options: ['Getting better', 'Getting worse', 'Staying the same'],
      showWhen: { field: 'hasTightness', equals: 'Yes' }
    },

    // Weakness & Function
    hasWeakness: {
      type: 'radio',
      label: 'Does your leg feel weak?',
      options: ['Yes', 'No']
    },

    walkingDifficulty: {
      type: 'radio',
      label: 'Can you walk normally?',
      options: ['Yes', 'Yes but it hurts', 'No, I limp']
    },

    runningDifficulty: {
      type: 'radio',
      label: 'Can you run?',
      options: ['Yes', 'Yes but it hurts', 'No']
    },

    stairsDifficulty: {
      type: 'radio',
      label: 'Can you climb stairs?',
      options: ['Yes', 'Yes but it\'s hard', 'No']
    },

    bendingDifficulty: {
      type: 'radio',
      label: 'Can you bend forward to touch your toes?',
      options: ['Yes', 'Partly', 'No, it\'s too painful']
    },

    sportDifficulty: {
      type: 'radio',
      label: 'Can you do your normal sport/exercise?',
      options: ['Yes', 'Yes but at reduced level', 'No']
    },

    givingWay: {
      type: 'radio',
      label: 'Does your leg feel unstable or give way?',
      options: ['Yes', 'No']
    },

    // Previous History
    previousHamstringInjury: {
      type: 'radio',
      label: 'Have you injured your hamstring before?',
      options: ['Yes', 'No']
    },

    previousInjuryCount: {
      type: 'radio',
      label: 'How many times have you injured your hamstring before?',
      options: ['1', '2', '3', '4 or more'],
      showWhen: { field: 'previousHamstringInjury', equals: 'Yes' }
    },

    timeSinceLastInjury: {
      type: 'text',
      label: 'How long ago was your last hamstring injury?',
      showWhen: { field: 'previousHamstringInjury', equals: 'Yes' }
    },

    previousRehab: {
      type: 'radio',
      label: 'Did you complete rehabilitation after your previous injury?',
      options: ['Yes', 'No', 'Partly'],
      showWhen: { field: 'previousHamstringInjury', equals: 'Yes' }
    },

    otherLegInjuries: {
      type: 'text',
      label: 'Have you had any other leg injuries? (hip, knee, calf, back)',
      placeholder: 'Tell us about any other injuries'
    },

    backProblems: {
      type: 'radio',
      label: 'Do you have any lower back problems?',
      options: ['Yes', 'No']
    },

    backProblemsDetails: {
      type: 'text',
      label: 'Tell us about your back problems:',
      showWhen: { field: 'backProblems', equals: 'Yes' }
    },

    // Training History
    sport: {
      type: 'text',
      label: 'What sports or exercise do you do?',
      placeholder: 'e.g., running, football, sprinting, gym'
    },

    trainingFrequency: {
      type: 'text',
      label: 'How often do you train/exercise?',
      placeholder: 'e.g., 3 times per week'
    },

    recentTrainingChange: {
      type: 'radio',
      label: 'Have you recently increased how much or how hard you train?',
      options: ['Yes', 'No']
    },

    warmUpRoutine: {
      type: 'radio',
      label: 'Do you warm up and stretch before exercise?',
      options: ['Yes, always', 'Sometimes', 'No']
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Diabetes',
        'Nerve problems',
        'Inflammatory conditions',
        'None of these'
      ]
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies'
    },

    // Lifestyle
    occupation: {
      type: 'text',
      label: 'What is your job?',
      placeholder: 'Tell us what you do for work'
    },

    smoking: {
      type: 'radio',
      label: 'Do you smoke?',
      options: ['No, never smoked', 'I used to smoke', 'Yes, I smoke']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. About Your Hamstring Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'onsetType', 'mainProblem', 'mainProblemOther', 'injuryDescription']
    },
    {
      id: 'mechanism',
      title: '3. How It Happened',
      component: 'GenericSection',
      enabled: true,
      fields: ['activityWhenInjured', 'feltPop', 'immediateSymptoms', 'injuryType']
    },
    {
      id: 'pain',
      title: '4. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painType', 'painTypeOther', 'painSeverity', 'painRadiation', 'painTiming', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'swelling',
      title: '5. Swelling & Bruising',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasSwelling', 'swellingLocation', 'hasBruising', 'bruisingLocation', 'bruisingTiming']
    },
    {
      id: 'tightness',
      title: '6. Tightness',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasTightness', 'tightnessTiming', 'tightnessChange']
    },
    {
      id: 'functional',
      title: '7. What You Can & Can\'t Do',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasWeakness', 'walkingDifficulty', 'runningDifficulty', 'stairsDifficulty', 'bendingDifficulty', 'sportDifficulty', 'givingWay']
    },
    {
      id: 'history',
      title: '8. Previous Injuries',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousHamstringInjury', 'previousInjuryCount', 'timeSinceLastInjury', 'previousRehab', 'otherLegInjuries', 'backProblems', 'backProblemsDetails']
    },
    {
      id: 'training',
      title: '9. Your Training',
      component: 'GenericSection',
      enabled: true,
      fields: ['sport', 'trainingFrequency', 'recentTrainingChange', 'warmUpRoutine']
    },
    {
      id: 'medical',
      title: '10. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true
    },    {
      id: 'exerciseVitalSign',
      title: '11. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '12. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly during activity': 'acute onset during activity',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Hamstring strain (Grade I)',
      'Hamstring strain (Grade II)',
      'Hamstring strain (Grade III/complete tear)',
      'Proximal hamstring tendinopathy',
      'Ischial tuberosity avulsion',
      'Referred pain from lumbar spine'
    ]
  }
};

export default hamstringTemplate;
